import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  fmt,
  fmtDate,
  type ReclassCategory,
  type ReclassEntryStatus,
  type ReclassificationEntryListRow,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  useActiveCompanyId,
  useCoa,
  useCreateReclassEntry,
  usePostReclassEntry,
  useReclassEntries,
  useReverseReclassNextPeriod,
} from '../lib/queries';

// =============================================================================
// Reclassification — period-end JV that flips balances between accounts for
// presentation purposes. Spec §36. Auto-reverses in the next period when
// reverseAtNextPeriod is true.
//
// Prototype was a pure-mock 5-row table with 4 tabs (Register / Cross-Check /
// Impact / Audit). This rewrite preserves the DOM shape and KPI layout but
// drives the data off the API:
//   - KPI cards reflect the live filtered list (count by status/category).
//   - Register tab → useReclassEntries, with status-tabbed views (Draft/Posted/
//     Reversed/All), and the "ดู" + เพิ่ม actions wired to the existing modal.
//   - Cross-Check / Impact / Audit tabs are kept as static prototype panels —
//     they pull derived analytics that aren't part of this slice. Future
//     phases can wire them off the same hook.
// =============================================================================

type TabKey = 'register' | 'crosscheck' | 'impact' | 'audit';
type StatusTab = 'all' | 'DRAFT' | 'POSTED' | 'REVERSED';
type SectionKey = 'BS-Asset' | 'BS-Liab' | 'BS-Eq' | 'PL';

interface RCCategory {
  code: ReclassCategory;
  label: string;
  tas: string;
  tone: string;
}

const RC_CATS: readonly RCCategory[] = [
  { code: 'CURRENT_LT', label: 'Current portion of LT Loan', tas: 'TAS 1.69', tone: '#1a56a6' },
  { code: 'DOUBTFUL', label: 'ลูกหนี้สงสัยจะสูญ', tas: 'TAS 1.78', tone: '#b54708' },
  { code: 'HELD_SALE', label: 'Asset Held for Sale', tas: 'TFRS 5', tone: '#7030A0' },
  { code: 'DEPOSIT_LT', label: 'เงินมัดจำลูกค้า > 1 ปี', tas: 'TAS 1.69', tone: '#0e7490' },
  { code: 'INVEST_CAT', label: 'เงินลงทุน — เปลี่ยน Category', tas: 'TFRS 9', tone: '#117a3d' },
  { code: 'PROVISION', label: 'Provision Re-class', tas: 'TAS 37', tone: '#a16207' },
  { code: 'REVENUE_TYPE', label: 'Reclass ระหว่างประเภทรายได้', tas: 'TFRS 15', tone: '#b42318' },
  { code: 'OTHER', label: 'อื่นๆ ตามที่ Auditor แนะนำ', tas: '—', tone: '#52525b' },
] as const;

const FALLBACK_CAT: RCCategory = {
  code: 'OTHER',
  label: 'อื่นๆ ตามที่ Auditor แนะนำ',
  tas: '—',
  tone: '#52525b',
};

function getCat(code: string): RCCategory {
  return RC_CATS.find((x) => x.code === code) ?? FALLBACK_CAT;
}

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

const CONCEPT_STEPS: readonly { n: string; t: string; d: string; c: string }[] = [
  {
    n: '1',
    t: 'Real GL Posting',
    d: 'JV Dr to / Cr from — เก็บประวัติใน Ledger',
    c: '#1a56a6',
  },
  {
    n: '2',
    t: 'Dr = Cr Balanced',
    d: 'JV เคารพ Balance ของระบบบัญชี',
    c: '#117a3d',
  },
  {
    n: '3',
    t: 'Auto-reverse N+1',
    d: 'เริ่มงวดถัดไป กลับรายการอัตโนมัติ',
    c: '#b54708',
  },
  {
    n: '4',
    t: 'Audit-friendly',
    d: 'มี code RC-YYYY-NNNNNN · linked JV · status',
    c: '#7030A0',
  },
] as const;

const TABS: readonly (readonly [TabKey, string])[] = [
  ['register', 'รายการ Reclass'],
  ['crosscheck', 'Cross-Check Dashboard'],
  ['impact', 'Impact on FS · Before/After'],
  ['audit', 'Audit Trail'],
] as const;

const STATUS_TABS: readonly (readonly [StatusTab, string])[] = [
  ['all', 'ทั้งหมด'],
  ['DRAFT', 'Draft'],
  ['POSTED', 'Posted'],
  ['REVERSED', 'Reversed'],
] as const;

// Default period = current YYYYMM (UTC, matches API contract).
function currentPeriod(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function statusBadge(s: ReclassEntryStatus): { l: string; c: string } {
  if (s === 'POSTED') return { l: 'POSTED', c: 'positive' };
  if (s === 'REVERSED') return { l: 'REVERSED', c: 'neutral' };
  return { l: 'DRAFT', c: 'warning' };
}

function toAmount(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export const Route = createFileRoute('/reclass')({
  component: ReclassPage,
});

function ReclassPage() {
  const companyId = useActiveCompanyId();
  const [tab, setTab] = useState<TabKey>('register');
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [showAdd, setShowAdd] = useState<boolean>(false);
  const [showDetail, setShowDetail] = useState<ReclassificationEntryListRow | null>(null);
  const [period, setPeriod] = useState<string>(currentPeriod());

  const entriesQuery = useReclassEntries(companyId, { period });
  const reverseMut = useReverseReclassNextPeriod(companyId);

  const allEntries: ReclassificationEntryListRow[] = useMemo(
    () => entriesQuery.data ?? [],
    [entriesQuery.data],
  );

  const stats = useMemo(() => {
    const total = allEntries.length;
    const draft = allEntries.filter((r) => r.status === 'DRAFT').length;
    const posted = allEntries.filter((r) => r.status === 'POSTED').length;
    const reversed = allEntries.filter((r) => r.status === 'REVERSED').length;
    const totalAmount = allEntries.reduce((s, r) => s + toAmount(r.amount), 0);
    const postedAmount = allEntries
      .filter((r) => r.status === 'POSTED')
      .reduce((s, r) => s + toAmount(r.amount), 0);
    return { total, draft, posted, reversed, totalAmount, postedAmount };
  }, [allEntries]);

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'รายการจัดประเภท (Reclassification)']}
        title="รายการจัดประเภท (Reclassification) — Period-end JVs"
        actions={
          <>
            <HelpButton guide="reclass" />
            <select className="select" value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value={currentPeriod()}>งวด {currentPeriod()}</option>
              {period !== currentPeriod() ? <option value={period}>{period}</option> : null}
            </select>
            <button
              className="btn"
              disabled={!companyId || reverseMut.isPending || stats.posted === 0}
              onClick={() => {
                if (!companyId) return;
                reverseMut.mutate({ data: { period } });
              }}
              title="กลับรายการ POSTED ในงวดถัดไป (auto-reverse)"
            >
              <Icon name="refresh" size={14} /> Reverse next period
            </button>
            <button className="btn primary" onClick={() => setShowAdd(true)} disabled={!companyId}>
              <Icon name="plus" size={14} /> เพิ่มรายการ Reclass
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            รายการในงวดนี้
          </div>
          <div className="value tnum">
            {stats.total} <span className="unit">รายการ</span>
          </div>
          <div className="trend faint">
            Draft {stats.draft} · Posted {stats.posted} · Reversed {stats.reversed}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand-600)' }} />
            ยอดรวม
          </div>
          <div className="value tnum">{fmt(stats.totalAmount)}</div>
          <div className="trend faint">บาท · ทุกสถานะ</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            รอ Post
          </div>
          <div className="value tnum">
            {stats.draft} <span className="unit">รายการ</span>
          </div>
          <div className="trend faint">Draft รออนุมัติ / Post JV</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            Posted (มี JV)
          </div>
          <div className="value tnum">
            {stats.posted} <span className="unit">รายการ</span>
          </div>
          <div className="trend faint">ยอด {fmt(stats.postedAmount)} บาท</div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          หลักการ — Reclassification Lifecycle
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          {CONCEPT_STEPS.map((s) => (
            <div
              key={s.n}
              style={{
                display: 'flex',
                gap: 10,
                padding: '10px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid var(--border)',
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: s.c,
                  color: 'white',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontFamily: 'var(--font-display)',
                  flexShrink: 0,
                }}
              >
                {s.n}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.t}</div>
                <div className="meta" style={{ marginTop: 2 }}>
                  {s.d}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="page-tabs">
        {TABS.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'register' && (
        <RCList
          entries={allEntries}
          statusTab={statusTab}
          setStatusTab={setStatusTab}
          isPending={entriesQuery.isPending}
          onDetail={setShowDetail}
          companyId={companyId}
        />
      )}
      {tab === 'crosscheck' && <CrossCheck entries={allEntries} />}
      {tab === 'impact' && <ImpactFS entries={allEntries} />}
      {tab === 'audit' && <AuditTrail entries={allEntries} />}

      {showAdd && (
        <AddReclassModal companyId={companyId} period={period} onClose={() => setShowAdd(false)} />
      )}
      {showDetail && (
        <DetailModal r={showDetail} companyId={companyId} onClose={() => setShowDetail(null)} />
      )}
    </div>
  );
}

// =============================================================================
// Register tab — 4 status tabs (All / Draft / Posted / Reversed) +
// 8-category breakdown above the table.
// =============================================================================

interface RCListProps {
  entries: readonly ReclassificationEntryListRow[];
  statusTab: StatusTab;
  setStatusTab: (s: StatusTab) => void;
  isPending: boolean;
  onDetail: (r: ReclassificationEntryListRow) => void;
  companyId: string | undefined;
}

function RCList({ entries, statusTab, setStatusTab, isPending, onDetail, companyId }: RCListProps) {
  const filtered = useMemo(() => {
    if (statusTab === 'all') return entries;
    return entries.filter((r) => r.status === statusTab);
  }, [entries, statusTab]);

  const catBreakdown = useMemo(() => {
    return RC_CATS.map((cat) => {
      const rows = entries.filter((r) => r.category === cat.code);
      const total = rows.reduce((s, r) => s + toAmount(r.amount), 0);
      return { ...cat, count: rows.length, total };
    });
  }, [entries]);

  return (
    <>
      {/* 8-category breakdown chips */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4,1fr)',
          gap: 10,
          marginBottom: 'var(--section-gap)',
        }}
      >
        {catBreakdown.map((c) => (
          <div
            key={c.code}
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: c.count > 0 ? 'white' : '#fafafa',
              opacity: c.count > 0 ? 1 : 0.6,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: c.tone,
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.label}
              </div>
              <div className="meta" style={{ fontSize: 11 }}>
                {c.tas} · {c.count} รายการ · {fmt(c.total)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="tbl-shell">
        <div className="filter-row" style={{ gap: 8, alignItems: 'center' }}>
          {STATUS_TABS.map(([k, l]) => (
            <button
              key={k}
              type="button"
              className={`btn sm ${statusTab === k ? 'primary' : 'ghost'}`}
              onClick={() => setStatusTab(k)}
            >
              {l}
            </button>
          ))}
          <input
            className="input search w-lg"
            placeholder="ค้นหา code / รายละเอียด"
            style={{
              marginLeft: 'auto',
              paddingLeft: 32,
              backgroundImage: SEARCH_BG,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: '10px center',
            }}
          />
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่</th>
              <th>งวด</th>
              <th>หมวด</th>
              <th>รายละเอียด</th>
              <th>From → To</th>
              <th className="num">จำนวนเงิน</th>
              <th>Auto-reverse</th>
              <th>สถานะ</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {isPending && (
              <tr>
                <td colSpan={9} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            )}
            {!isPending && !companyId && (
              <tr>
                <td colSpan={9} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  เลือกบริษัทเพื่อดูรายการ
                </td>
              </tr>
            )}
            {!isPending && companyId && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ยังไม่มีข้อมูล — กดปุ่ม "<b>+ เพิ่มรายการ Reclass</b>" เพื่อสร้างรายการแรก
                </td>
              </tr>
            )}
            {filtered.map((r) => {
              const c = getCat(r.category);
              const s = statusBadge(r.status);
              return (
                <tr key={r.id} onClick={() => onDetail(r)} style={{ cursor: 'pointer' }}>
                  <td className="code">
                    <b>{r.code}</b>
                  </td>
                  <td>{r.period}</td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: c.tone }} />
                      {c.label}
                    </span>
                    <div className="meta">{c.tas}</div>
                  </td>
                  <td>{r.description}</td>
                  <td className="code muted">
                    {r.fromAccountCode} → {r.toAccountCode}
                    {r.fromAccountName || r.toAccountName ? (
                      <div className="meta" style={{ whiteSpace: 'normal' }}>
                        {r.fromAccountName ?? '—'} → {r.toAccountName ?? '—'}
                      </div>
                    ) : null}
                  </td>
                  <td className="num tnum">{fmt(toAmount(r.amount))}</td>
                  <td>
                    {r.reverseAtNextPeriod ? (
                      <span className="badge info">N+1</span>
                    ) : (
                      <span className="badge neutral">Permanent</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${s.c}`}>{s.l}</span>
                    {r.postedAt && r.status !== 'DRAFT' ? (
                      <div className="meta">{fmtDate(String(r.postedAt))}</div>
                    ) : null}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn sm ghost"
                      style={{ height: 26, fontSize: 13 }}
                      onClick={() => onDetail(r)}
                    >
                      ดู
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =============================================================================
// Cross-Check tab — Dr=Cr per entry (always true for our shape) + by-section
// rollups derived from the live data. Section is inferred from the COA prefix
// when not otherwise known (loose heuristic).
// =============================================================================

function inferSection(accountCode: string): SectionKey {
  if (accountCode.startsWith('1')) return 'BS-Asset';
  if (accountCode.startsWith('2')) return 'BS-Liab';
  if (accountCode.startsWith('3')) return 'BS-Eq';
  return 'PL';
}

function CrossCheck({ entries }: { entries: readonly ReclassificationEntryListRow[] }) {
  const drCrRows = entries.map((r) => {
    const amt = toAmount(r.amount);
    // Per our model: Dr to / Cr from, so Dr === Cr === amount always.
    return { id: r.code, entity: '—', dr: amt, cr: amt, diff: 0, pass: true };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
      <div className="card">
        <div className="card-head">
          <div>
            <h3>① Dr = Cr ทุกรายการ</h3>
            <div className="sub">
              JV ที่ post ผ่าน journal-entries service ผ่าน balance check อยู่แล้ว
            </div>
          </div>
          {drCrRows.every((r) => r.pass) ? (
            <span className="badge positive" style={{ fontSize: 14 }}>
              <Icon name="check" size={10} /> ผ่านทั้งหมด
            </span>
          ) : (
            <span className="badge negative">
              <span className="bdot" />
              ไม่ผ่าน
            </span>
          )}
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>เลขที่ Reclass</th>
                <th className="num">Dr</th>
                <th className="num">Cr</th>
                <th className="num">ผลต่าง</th>
                <th>ผลลัพธ์</th>
              </tr>
            </thead>
            <tbody>
              {drCrRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                    ไม่มีรายการในงวดนี้
                  </td>
                </tr>
              ) : (
                drCrRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <b>{r.id}</b>
                    </td>
                    <td className="num tnum">{fmt(r.dr)}</td>
                    <td className="num tnum">{fmt(r.cr)}</td>
                    <td className="num tnum">
                      <b style={{ color: 'var(--positive)' }}>0 ✓</b>
                    </td>
                    <td>
                      <span className="badge positive">✓ Pass</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>② By-section rollup</h3>
        </div>
        <div className="card-body">
          <table
            className="tbl"
            style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}
          >
            <thead>
              <tr>
                <th>Section</th>
                <th className="num">รวม In (Dr to)</th>
                <th className="num">รวม Out (Cr from)</th>
              </tr>
            </thead>
            <tbody>
              {(['BS-Asset', 'BS-Liab', 'BS-Eq', 'PL'] as const).map((section) => {
                const inSum = entries
                  .filter((r) => inferSection(r.toAccountCode) === section)
                  .reduce((s, r) => s + toAmount(r.amount), 0);
                const outSum = entries
                  .filter((r) => inferSection(r.fromAccountCode) === section)
                  .reduce((s, r) => s + toAmount(r.amount), 0);
                return (
                  <tr key={section}>
                    <td>{section}</td>
                    <td className="num tnum">{fmt(inSum)}</td>
                    <td className="num tnum">{fmt(outSum)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Impact tab — derived from live entries (compact view).
// =============================================================================

function ImpactFS({ entries }: { entries: readonly ReclassificationEntryListRow[] }) {
  // Aggregate net diff per account code across all POSTED entries.
  const byAccount = new Map<string, { name: string; diff: number; from: string }>();
  for (const r of entries) {
    if (r.status === 'DRAFT') continue;
    const amt = toAmount(r.amount);
    const inEntry = byAccount.get(r.toAccountCode) ?? {
      name: r.toAccountName ?? r.toAccountCode,
      diff: 0,
      from: r.code,
    };
    inEntry.diff += amt;
    inEntry.from = r.code;
    byAccount.set(r.toAccountCode, inEntry);
    const outEntry = byAccount.get(r.fromAccountCode) ?? {
      name: r.fromAccountName ?? r.fromAccountCode,
      diff: 0,
      from: r.code,
    };
    outEntry.diff -= amt;
    outEntry.from = r.code;
    byAccount.set(r.fromAccountCode, outEntry);
  }
  const items = Array.from(byAccount.entries()).map(([code, v]) => ({
    acct: `${code} ${v.name}`,
    diff: v.diff,
    from: v.from,
  }));

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>เปรียบเทียบยอด — Net Diff ต่อบัญชี</h3>
          <div className="sub">รวมเฉพาะ POSTED + REVERSED ในงวดที่เลือก</div>
        </div>
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>บัญชี</th>
              <th className="num">Diff</th>
              <th>มาจาก</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={3} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ยังไม่มีรายการที่ POSTED ในงวดนี้
                </td>
              </tr>
            ) : (
              items.map((r) => (
                <tr key={r.acct}>
                  <td>
                    <b>{r.acct}</b>
                  </td>
                  <td
                    className="num tnum"
                    style={{
                      color: r.diff > 0 ? 'var(--positive)' : 'var(--negative)',
                      fontWeight: 600,
                    }}
                  >
                    {r.diff > 0 ? '+' : ''}
                    {fmt(r.diff)}
                  </td>
                  <td className="code muted">{r.from}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Audit tab — flatten lifecycle events (created → posted → reversed).
// =============================================================================

interface AuditEvent {
  date: string;
  id: string;
  action: 'สร้าง' | 'Post' | 'Reverse';
  by: string;
  note: string;
}

function AuditTrail({ entries }: { entries: readonly ReclassificationEntryListRow[] }) {
  const events: AuditEvent[] = [];
  for (const r of entries) {
    events.push({
      date: String(r.createdAt),
      id: r.code,
      action: 'สร้าง',
      by: r.createdBy ?? 'system',
      note: r.description.slice(0, 70),
    });
    if (r.postedAt) {
      events.push({
        date: String(r.postedAt),
        id: r.code,
        action: 'Post',
        by: r.postedBy ?? 'system',
        note: `JV posted (Dr ${r.toAccountCode} / Cr ${r.fromAccountCode})`,
      });
    }
    if (r.reversedAt) {
      events.push({
        date: String(r.reversedAt),
        id: r.code,
        action: 'Reverse',
        by: r.reversedBy ?? 'system',
        note: 'Auto-reversed in next period',
      });
    }
  }
  events.sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>Audit Trail — ประวัติ Reclass ทั้งหมด</span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>วันที่</th>
            <th>เลขที่</th>
            <th>Action</th>
            <th>ผู้กระทำ</th>
            <th>หมายเหตุ</th>
          </tr>
        </thead>
        <tbody>
          {events.length === 0 ? (
            <tr>
              <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                ยังไม่มี audit trail
              </td>
            </tr>
          ) : (
            events.map((e, i) => (
              <tr key={`${e.id}-${e.action}-${i}`}>
                <td>{fmtDate(e.date)}</td>
                <td className="code">
                  <b>{e.id}</b>
                </td>
                <td>
                  <span
                    className={`badge ${e.action === 'สร้าง' ? 'info' : e.action === 'Post' ? 'positive' : 'neutral'}`}
                  >
                    {e.action}
                  </span>
                </td>
                <td className="muted">{e.by}</td>
                <td className="muted">{e.note}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Add modal — wires from/to selectors to useCoa (leaf accounts only — see
// filter below). The prototype's multi-line Dr/Cr is collapsed into a single
// (from, to, amount) pair since that's the API shape. Multi-line reclasses
// are modelled as multiple entries grouped by description.
// =============================================================================

function AddReclassModal({
  companyId,
  period,
  onClose,
}: {
  companyId: string | undefined;
  period: string;
  onClose: () => void;
}) {
  const [category, setCategory] = useState<ReclassCategory>('CURRENT_LT');
  const [description, setDescription] = useState<string>('');
  const [fromAccountCode, setFromAccountCode] = useState<string>('');
  const [toAccountCode, setToAccountCode] = useState<string>('');
  const [amount, setAmount] = useState<string>('0');
  const [reverseAtNextPeriod, setReverseAtNextPeriod] = useState<boolean>(true);

  const coaQuery = useCoa(companyId);
  const createMut = useCreateReclassEntry(companyId);
  const postMut = usePostReclassEntry(companyId);

  // Postable leaf accounts only — headers/aggregators (control accounts) are
  // not bookable. Every real account is a leaf with isControl=false, so the
  // postable set is simply the non-control, active accounts.
  const leafAccounts = useMemo(() => {
    const all = coaQuery.data ?? [];
    return all.filter((a) => a.isActive && !a.isControl);
  }, [coaQuery.data]);

  const amountNum = Number.parseFloat(amount) || 0;
  const canSubmit =
    !!companyId &&
    description.trim().length > 0 &&
    fromAccountCode.length > 0 &&
    toAccountCode.length > 0 &&
    fromAccountCode !== toAccountCode &&
    amountNum > 0 &&
    !createMut.isPending &&
    !postMut.isPending;

  const submit = async (postImmediately: boolean) => {
    if (!canSubmit || !companyId) return;
    const created = await createMut.mutateAsync({
      period,
      category,
      description,
      fromAccountCode,
      toAccountCode,
      amount: amountNum,
      reverseAtNextPeriod,
    });
    if (postImmediately) {
      await postMut.mutateAsync({ id: created.id });
    }
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 760,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{ margin: 0, fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)' }}
          >
            เพิ่มรายการ Reclassification — งวด {period}
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: '22px',
            overflow: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              หมวด (Category)
            </label>
            <select
              className="select"
              value={category}
              onChange={(e) => setCategory(e.target.value as ReclassCategory)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              {RC_CATS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label} — {c.tas}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              รายละเอียด
            </label>
            <textarea
              className="input"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="เช่น ย้ายเงินกู้ระยะยาว 240M ที่ครบกำหนดใน 12 เดือนข้างหน้า → ระยะสั้น"
              style={{ width: '100%', padding: 10, fontSize: 14, minHeight: 60 }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                บัญชี From (Cr)
              </label>
              <select
                className="select"
                value={fromAccountCode}
                onChange={(e) => setFromAccountCode(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                <option value="">— เลือกบัญชี —</option>
                {leafAccounts.map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.code} · {a.nameTh}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                บัญชี To (Dr)
              </label>
              <select
                className="select"
                value={toAccountCode}
                onChange={(e) => setToAccountCode(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                <option value="">— เลือกบัญชี —</option>
                {leafAccounts.map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.code} · {a.nameTh}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                จำนวนเงิน (บาท)
              </label>
              <input
                className="input tnum"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Auto-reverse งวดถัดไป
              </label>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, fontSize: 14 }}
              >
                <input
                  type="checkbox"
                  checked={reverseAtNextPeriod}
                  onChange={(e) => setReverseAtNextPeriod(e.target.checked)}
                />
                {reverseAtNextPeriod ? 'ใช่ (auto-reverse)' : 'ไม่ (permanent)'}
              </label>
            </div>
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#fafafa',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 14,
            }}
          >
            <b>JV preview:</b> Dr <span className="code">{toAccountCode || '—'}</span>{' '}
            {fmt(amountNum)} / Cr <span className="code">{fromAccountCode || '—'}</span>{' '}
            {fmt(amountNum)}
          </div>

          {createMut.error || postMut.error ? (
            <div
              style={{
                padding: '10px 12px',
                background: '#fce9e7',
                border: '1px solid var(--negative)',
                borderRadius: 6,
                fontSize: 13,
                color: 'var(--negative)',
              }}
            >
              {(createMut.error as Error | undefined)?.message ??
                (postMut.error as Error | undefined)?.message}
            </div>
          ) : null}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn"
            disabled={!canSubmit}
            onClick={() => {
              void submit(false);
            }}
          >
            บันทึก Draft
          </button>
          <button
            className="btn primary"
            disabled={!canSubmit}
            onClick={() => {
              void submit(true);
            }}
          >
            <Icon name="check" size={14} /> บันทึก + Post JV
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Detail modal — read-only view + Post action for DRAFT entries.
// =============================================================================

function DetailModal({
  r,
  companyId,
  onClose,
}: {
  r: ReclassificationEntryListRow;
  companyId: string | undefined;
  onClose: () => void;
}) {
  const c = getCat(r.category);
  const postMut = usePostReclassEntry(companyId);
  const amount = toAmount(r.amount);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 720,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              {r.code} — {c.label}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {c.tas} · งวด {r.period} · {r.status}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            <div className="meta">รายละเอียด:</div>
            <div style={{ marginTop: 4 }}>{r.description}</div>
            <div className="meta" style={{ marginTop: 8 }}>
              Auto-reverse:{' '}
              <b style={{ color: '#3f3f46' }}>
                {r.reverseAtNextPeriod ? 'ใช่ (N+1)' : 'ไม่ (permanent)'}
              </b>
            </div>
          </div>

          <table
            className="tbl"
            style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}
          >
            <thead>
              <tr>
                <th>บัญชี</th>
                <th>ชื่อ</th>
                <th className="num">Dr</th>
                <th className="num">Cr</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="code">{r.toAccountCode}</td>
                <td>{r.toAccountName ?? '—'}</td>
                <td className="num tnum">{fmt(amount)}</td>
                <td className="num tnum">—</td>
              </tr>
              <tr>
                <td className="code">{r.fromAccountCode}</td>
                <td>{r.fromAccountName ?? '—'}</td>
                <td className="num tnum">—</td>
                <td className="num tnum">{fmt(amount)}</td>
              </tr>
              <tr className="total">
                <td colSpan={2}>รวม</td>
                <td className="num tnum">{fmt(amount)}</td>
                <td className="num tnum">{fmt(amount)}</td>
              </tr>
            </tbody>
          </table>

          {r.journalEntryId ? (
            <div
              style={{
                padding: '10px 12px',
                background: '#e7f4ec',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              <b>JV posted:</b> <span className="code">{r.journalEntryId.slice(0, 8)}…</span>
              {r.reversalJournalEntryId ? (
                <>
                  {' · '}
                  <b>Reversal JV:</b>{' '}
                  <span className="code">{r.reversalJournalEntryId.slice(0, 8)}…</span>
                </>
              ) : null}
            </div>
          ) : (
            <div
              style={{
                padding: '10px 12px',
                background: '#fef3c7',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              <b>Draft</b> · ยังไม่มี JV — กด "Post" เพื่อสร้าง JV จริง
            </div>
          )}

          {postMut.error ? (
            <div
              style={{
                padding: '10px 12px',
                background: '#fce9e7',
                borderRadius: 6,
                fontSize: 13,
                color: 'var(--negative)',
              }}
            >
              {postMut.error.message}
            </div>
          ) : null}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {r.status === 'DRAFT' ? (
            <button
              className="btn primary"
              disabled={postMut.isPending}
              onClick={() => {
                postMut.mutate(
                  { id: r.id },
                  {
                    onSuccess: () => onClose(),
                  },
                );
              }}
            >
              <Icon name="check" size={14} /> Post JV
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
